"use client";
import { useEffect, useMemo, useState } from 'react';
import Image from 'next/image';
import Link from 'next/link';
import PropTypes from 'prop-types';
import { useRouter } from 'next/navigation';
import { motion } from 'framer-motion';
import { Heart, MessageCircle, Share2, ShoppingCart } from 'lucide-react';
import { flushSync } from 'react-dom';
import toast from 'react-hot-toast';
import { useCart } from '@/context/CartContext';
import { useWishlist } from '@/context/WishlistContext';
import { trackViewContent } from '@/lib/analytics';
import {
  getProductAvailabilityError,
  getProductStockLabel,
  isProductUnavailable,
} from '@/lib/productAvailability';
import { getVariantSizeStock, getVariantTotalStock, hasVariantInventory } from '@/lib/productInventory';
import { getDisplayProductDetails } from '@/lib/productDetails';
import { parseProductDescription } from '@/lib/productDescription';
import {
  buildCatalogPath,
  buildProductPath,
  findProductVariant,
  formatProductCategory,
  getProductDepartment,
  getProductSubdivision,
  getVisibleSubdivisionOptions,
} from '@/lib/productRoutes';
import { getProductPricing } from '@/lib/productPricing';
import { getProductReviews, getReviewAggregate } from '@/lib/customerReviews';
import { useCustomerAuth } from '@/context/CustomerAuthContext';

const buildOrderWhatsAppMessage = ({ product, selectedItems }) => {
  const descriptionSummary = parseProductDescription(product?.description, product?.name).summary;
  const { regularPrice, salePrice, hasDiscount, discount } = getProductPricing(product);
  const orderLines = selectedItems.map((item) =>
    [
      `Color: ${item.color || 'Default'}`,
      item.size ? `Size: ${item.size}` : null,
      `Quantity: ${item.quantity}`,
    ]
      .filter(Boolean)
      .join(' | ')
  );

  return [
    'Hi Trendy Quest!',
    '',
    `Product: ${product?.name || ''}`,
    descriptionSummary ? `Description: ${descriptionSummary}` : null,
    `Category: ${formatProductCategory(product)}`,
    `Price: KES ${salePrice}`,
    hasDiscount ? `Regular price: KES ${regularPrice}` : null,
    hasDiscount ? `Automatic discount: ${discount}%` : null,
    '',
    'Order Selection:',
    ...orderLines,
    '',
    'Delivery Details:',
    'Customer Name:',
    'Phone Number:',
    'Delivery Address:',
  ]
    .filter(Boolean)
    .join('\n');
};

const getOptionKey = (option, index) => String(option?.name || option?.image || `default-${index}`);

export default function ProductDetailPageView({
  product,
  recommendedProducts = [],
  selectedColorSlug = '',
}) {
  const router = useRouter();
  const [selectedVariant, setSelectedVariant] = useState(null);
  const [selectedImage, setSelectedImage] = useState('');
  const [lineSelections, setLineSelections] = useState({});
  const [isAdding, setIsAdding] = useState(false);
  const [hasTrackedView, setHasTrackedView] = useState(false);
  const { addToCart } = useCart();
  const { toggleWishlist, isInWishlist } = useWishlist();
  const { user, isSignedIn } = useCustomerAuth();
  const [reviews, setReviews] = useState(() => getProductReviews(product, 10));
  const [reviewText, setReviewText] = useState('');
  const [reviewRating, setReviewRating] = useState(5);
  const [reviewSubmitting, setReviewSubmitting] = useState(false);

  useEffect(() => {
    setIsAdding(false);
    setHasTrackedView(false);

    if (!product) {
      setSelectedVariant(null);
      setSelectedImage('');
      setLineSelections({});
      return;
    }

    const matchedVariant = findProductVariant(product, selectedColorSlug);
    const fallbackVariant = product?.variants?.[0] || null;
    const nextVariant = matchedVariant || fallbackVariant;

    setSelectedVariant(nextVariant);
    setSelectedImage(nextVariant?.image || product?.image || '');
    const options = product?.variants?.length ? product.variants : [{ name: '', image: product.image }];
    const defaultSelectionKey = getOptionKey(nextVariant || options[0], 0);
    const nextSelections = {};

    options.forEach((option, index) => {
      const key = getOptionKey(option, index);
      nextSelections[key] = {
        quantity: key === defaultSelectionKey ? 1 : 0,
        size: product?.sizes?.[0] || '',
      };
    });

    setLineSelections(nextSelections);
  }, [product, selectedColorSlug]);

  useEffect(() => {
    setReviews(getProductReviews(product, 10));
  }, [product]);

  useEffect(() => {
    if (!product || hasTrackedView) return;
    if (trackViewContent(product)) {
      setHasTrackedView(true);
    }
  }, [product, hasTrackedView]);

  const galleryImages = useMemo(
    () =>
      (product ? [product.image, ...(product.variants?.map((variant) => variant.image) || [])] : [])
        .filter(Boolean)
        .filter((image, index, images) => images.indexOf(image) === index),
    [product]
  );

  const currentImage = selectedImage || selectedVariant?.image || product?.image;
  const descriptionContent = useMemo(
    () => parseProductDescription(product?.description, product?.name),
    [product?.description, product?.name]
  );
  const productDetails = useMemo(() => getDisplayProductDetails(product), [product]);
  const descriptionSummary = descriptionContent.summary;
  const { salePrice: resolvedSalePrice, regularPrice: resolvedOriginalPrice, hasDiscount: hasOriginalPrice, discount } =
    getProductPricing(product);
  const stockLabel = getProductStockLabel(product);
  const isUnavailable = isProductUnavailable(product);
  const isFavorite = product ? isInWishlist(product.id) : false;
  const orderOptions = useMemo(
    () => (product?.variants?.length ? product.variants : [{ name: '', image: product?.image || '' }]),
    [product]
  );
  const selectedOrderItems = useMemo(
    () =>
      orderOptions
        .map((option, index) => {
          const key = getOptionKey(option, index);
          const selection = lineSelections[key] || {};
          const quantity = Math.max(0, Number(selection.quantity) || 0);

          if (!quantity) return null;

          return {
            key,
            quantity,
            size: selection.size || '',
            color: option?.name || '',
            image: option?.image || product?.image || '',
          };
        })
        .filter(Boolean),
    [lineSelections, orderOptions, product?.image]
  );
  const selectedQuantity = selectedOrderItems.reduce((sum, item) => sum + item.quantity, 0);
  const department = getProductDepartment(product);
  const subdivision = getProductSubdivision(product);
  const hasSubdivision =
    Boolean(subdivision) &&
    getVisibleSubdivisionOptions({ department, sizeType: product?.sizeType }).length > 0;
  const detailRows = [
    { label: 'Department', value: department },
    { label: 'Stock', value: stockLabel },
    { label: 'Colors', value: product?.variants?.length ? String(product.variants.length) : '1' },
    { label: 'Sizes', value: product?.sizes?.length ? product.sizes.join(', ') : 'Ask on WhatsApp' },
  ];

  if (hasSubdivision) {
    detailRows.splice(1, 0, { label: 'Subdivision', value: subdivision });
  }

  if (product?.sku) {
    detailRows.splice(2, 0, { label: 'SKU', value: product.sku });
  }

  const updateVariantRoute = async (variant) => {
    if (!product) return;

    const targetPath = buildProductPath(product, { color: variant?.name });
    const currentPath = String(router.asPath || '').split('?')[0];

    if (!targetPath || targetPath === currentPath) return;

    try {
      await router.replace(targetPath, undefined, { scroll: false });
    } catch {
      // Keep the selection change even if route replacement fails.
    }
  };

  const updateLineSelection = (key, patch) => {
    setLineSelections((previous) => ({
      ...previous,
      [key]: {
        ...(previous[key] || {}),
        ...patch,
      },
    }));
  };

  const selectOrderVariant = (option, { syncRoute = false } = {}) => {
    setSelectedVariant(option);
    setSelectedImage(option?.image || product?.image || '');

    if (syncRoute) {
      void updateVariantRoute(option);
    }
  };

  const handleAddToCart = async ({ silent = false, redirectToCheckout = false } = {}) => {
    if (isUnavailable) {
      toast.error('This product is currently out of stock.');
      return false;
    }

    if (!selectedOrderItems.length) {
      toast.error('Select at least one color quantity before continuing.');
      return false;
    }

    if (product?.sizes?.length && selectedOrderItems.some((item) => !item.size)) {
      toast.error('Select a size for every color you are ordering.');
      return false;
    }

    const availabilityError = selectedOrderItems
      .map((item) => getProductAvailabilityError(product, item))
      .find(Boolean);
    if (availabilityError) {
      toast.error(availabilityError);
      return false;
    }

    flushSync(() => {
      setIsAdding(true);
      selectedOrderItems.forEach((item) => {
        addToCart(product, item.quantity, item.size || null, item.color || null);
      });
    });

    if (!silent) {
      toast.success(selectedQuantity > 1 ? 'Items added to cart!' : 'Added to cart!');
    }

    if (redirectToCheckout) {
      try {
        await router.push('/checkout');
      } finally {
        setIsAdding(false);
      }

      return true;
    }

    window.setTimeout(() => setIsAdding(false), 500);
    return true;
  };

  if (router.isFallback || !product) {
    return (
      <div className="flex min-h-screen items-center justify-center">
        <div className="h-12 w-12 animate-spin rounded-full border-4 border-teal-600 border-t-transparent" />
      </div>
    );
  }

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.35 }}
      className="min-h-screen bg-[#fbfaf5] py-8 sm:py-10 lg:py-12"
    >
      <div className="w-full px-3 sm:px-4 md:px-6">
        <div className="mx-auto w-full max-w-[1500px] space-y-6 sm:space-y-10">
          <nav className="flex flex-wrap items-center gap-2 text-sm text-[#667063]">
            <Link href="/" className="transition hover:text-[#24552d]">Home</Link>
            <span>/</span>
            <Link href="/products" className="transition hover:text-[#24552d]">Products</Link>
            <span>/</span>
            <Link href={buildCatalogPath({ department })} className="transition hover:text-[#24552d]">
              {department}
            </Link>
            {hasSubdivision ? (
              <>
                <span>/</span>
                <Link href={buildCatalogPath({ department, subcategory: subdivision })} className="transition hover:text-[#24552d]">
                  {subdivision}
                </Link>
              </>
            ) : null}
            <span>/</span>
            <span className="font-semibold text-[#223326]">{product.name}</span>
          </nav>

          <div className="grid min-w-0 items-start gap-5 sm:gap-8 xl:grid-cols-[minmax(0,1.08fr)_minmax(360px,0.92fr)]">
            <div className="space-y-4">
              <div className="overflow-hidden rounded-[1.25rem] border border-[#e7dfcf] bg-white shadow-[0_16px_45px_rgba(21,35,24,0.06)] sm:rounded-[2rem]">
                <div className="relative aspect-[1/1] overflow-hidden bg-[#f5f2e8]">
                  <Image
                    src={currentImage}
                    alt={product.name}
                    fill
                    sizes="(max-width: 1280px) 100vw, 52vw"
                    className="object-cover"
                  />
                </div>
              </div>

              {galleryImages.length > 1 ? (
                <div className="grid grid-cols-4 gap-2 rounded-[1.25rem] border border-[#e7dfcf] bg-white p-2 shadow-sm sm:grid-cols-5 sm:gap-3 sm:rounded-[1.5rem] sm:p-4">
                  {galleryImages.map((image, index) => (
                    <button
                      key={`${image}-${index}`}
                      type="button"
                      onClick={() => {
                        setSelectedImage(image);
                      }}
                      className={`relative aspect-square overflow-hidden rounded-xl border-2 transition sm:rounded-2xl ${
                        currentImage === image ? 'border-[#24552d]' : 'border-[#e7dfcf]'
                      }`}
                    >
                      <Image
                        src={image}
                        alt={`${product.name} ${index + 1}`}
                        fill
                        sizes="140px"
                        className="object-cover"
                      />
                    </button>
                  ))}
                </div>
              ) : null}
            </div>

            <div className="space-y-5 rounded-[1.25rem] border border-[#e7dfcf] bg-white p-4 shadow-[0_16px_45px_rgba(21,35,24,0.06)] sm:space-y-6 sm:rounded-[2rem] sm:p-8">
              <div>
                <p className="text-sm font-semibold uppercase tracking-[0.24em] text-[#2b5f34]">Current Product</p>
                <div className="mt-3 flex flex-wrap items-center gap-2">
                  <span className="rounded-full bg-[#f6f1e4] px-3 py-1.5 text-xs font-semibold uppercase tracking-[0.18em] text-[#35553a]">
                    {department}
                  </span>
                  {hasSubdivision ? (
                    <span className="rounded-full bg-[#f6f1e4] px-3 py-1.5 text-xs font-semibold uppercase tracking-[0.18em] text-[#35553a]">
                      {subdivision}
                    </span>
                  ) : null}
                  <span
                    className={`rounded-full px-3 py-1.5 text-xs font-semibold uppercase tracking-[0.18em] ${
                      !isUnavailable
                        ? 'bg-[#eef7ef] text-[#24552d]'
                        : 'bg-[#f3f4f6] text-[#6b7280]'
                    }`}
                  >
                    {stockLabel}
                  </span>
                </div>
                <h1 className="mt-4 text-3xl font-playfair font-bold text-[#182218] sm:text-4xl lg:text-5xl">
                  {product.name}
                </h1>
                {descriptionSummary ? (
                  <p className="mt-4 text-base leading-7 text-[#62675f] sm:text-lg">
                    {descriptionSummary}
                  </p>
                ) : null}
              </div>

              <div className="rounded-[1.5rem] border border-[#efe7d6] bg-[#fcfaf4] p-5">
                <div className="flex flex-wrap items-end gap-3">
                  <p className="text-3xl font-playfair font-bold text-[#1c6a43] sm:text-4xl">
                    KES {resolvedSalePrice}
                  </p>
                  {hasOriginalPrice ? (
                    <p className="pb-1 text-base text-[#8d9386] line-through">KES {resolvedOriginalPrice}</p>
                  ) : null}
                  {hasOriginalPrice ? (
                    <span className="rounded-full bg-[#24552d] px-3 py-1 text-xs font-semibold uppercase tracking-[0.18em] text-white">
                      -{discount}% off
                    </span>
                  ) : null}
                </div>
                <p className="mt-2 text-sm text-[#667063]">{formatProductCategory(product)}</p>
              </div>

              <section className="space-y-3">
                <div className="flex items-center justify-between gap-3">
                  <h2 className="text-lg font-semibold text-[#182218]">Choose colors and quantities</h2>
                  <span className="text-sm text-[#667063]">{selectedQuantity} selected</span>
                </div>
                <div className="space-y-3">
                  {orderOptions.map((option, index) => {
                    const key = getOptionKey(option, index);
                    const selection = lineSelections[key] || {};
                    const quantity = Math.max(0, Number(selection.quantity) || 0);
                    const optionImage = option?.image || product?.image || '';
                    const optionStock = hasVariantInventory(product)
                      ? getVariantTotalStock(product, option?.name || '')
                      : null;
                    const optionUnavailable = optionStock !== null && optionStock <= 0;

                    return (
                      <div
                        key={key}
                        className={`rounded-[1.1rem] border p-3 transition ${
                          quantity > 0
                            ? 'border-[#2f7b45] bg-[#f6fbf7]'
                            : 'border-[#e6dcc8] bg-[#fffdfa]'
                        }`}
                      >
                        <div className="grid gap-3 min-[460px]:grid-cols-[auto_minmax(0,1fr)_auto] min-[460px]:items-center">
                          <button
                            type="button"
                            onClick={() => selectOrderVariant(option, { syncRoute: true })}
                            className="relative h-16 w-16 shrink-0 overflow-hidden rounded-xl bg-[#f5f2e8]"
                            aria-label={`View ${option?.name || product.name}`}
                          >
                            <Image
                              src={optionImage}
                              alt={option?.name || product.name}
                              fill
                              sizes="64px"
                              className="object-cover"
                            />
                          </button>
                          <button
                            type="button"
                            onClick={() => selectOrderVariant(option, { syncRoute: true })}
                            className="min-w-0 flex-1 text-left"
                          >
                            <span className="block font-semibold text-[#182218]">
                              {option?.name || 'Default'}
                            </span>
                            <span className="block text-sm text-[#536052]">KES {resolvedSalePrice} each</span>
                          </button>
                          <div className="flex w-full items-center justify-between gap-2 rounded-xl border border-[#e6dcc8] bg-white p-1.5 min-[460px]:w-auto min-[460px]:justify-start">
                            <button
                              type="button"
                              onClick={() => {
                                selectOrderVariant(option);
                                updateLineSelection(key, { quantity: Math.max(0, quantity - 1) });
                              }}
                              disabled={optionUnavailable}
                              className="flex h-9 w-9 items-center justify-center rounded-lg text-base font-semibold text-[#24552d] transition hover:bg-[#f2ead8] disabled:cursor-not-allowed disabled:opacity-40"
                              aria-label={`Decrease ${option?.name || product.name} quantity`}
                            >
                              -
                            </button>
                            <input
                              type="number"
                              min="0"
                              max={optionStock === null ? undefined : optionStock}
                              disabled={optionUnavailable}
                              value={optionUnavailable ? 0 : quantity}
                              onFocus={() => selectOrderVariant(option)}
                              onChange={(event) => {
                                selectOrderVariant(option);
                                updateLineSelection(key, {
                                  quantity: optionStock === null
                                    ? Math.max(0, Number(event.target.value) || 0)
                                    : Math.min(optionStock, Math.max(0, Number(event.target.value) || 0)),
                                });
                              }}
                              className="h-9 w-14 rounded-lg border border-[#e6dcc8] bg-white text-center text-sm font-bold text-[#182218]"
                              aria-label={`${option?.name || 'Default'} quantity`}
                            />
                            <button
                              type="button"
                              onClick={() => {
                                selectOrderVariant(option);
                                updateLineSelection(key, {
                                  quantity: optionStock === null ? quantity + 1 : Math.min(optionStock, quantity + 1),
                                });
                              }}
                              disabled={optionUnavailable || (optionStock !== null && quantity >= optionStock)}
                              className="flex h-9 w-9 items-center justify-center rounded-lg text-base font-semibold text-[#24552d] transition hover:bg-[#f2ead8] disabled:cursor-not-allowed disabled:opacity-40"
                              aria-label={`Increase ${option?.name || product.name} quantity`}
                            >
                              +
                            </button>
                          </div>
                        </div>
                        {product?.sizes?.length && quantity > 0 ? (
                          <select
                            value={selection.size || ''}
                            onFocus={() => selectOrderVariant(option)}
                            onChange={(event) => {
                              selectOrderVariant(option);
                              updateLineSelection(key, { size: event.target.value });
                            }}
                            className="mt-3 w-full rounded-xl border border-[#e6dcc8] bg-white px-3 py-2 text-sm text-[#182218] focus:outline-none focus:ring-2 focus:ring-[#24552d]"
                            required
                          >
                            <option value="">Select size *</option>
                            {product.sizes.map((optionSize) => {
                              const sizeStock = hasVariantInventory(product)
                                ? getVariantSizeStock(product, option?.name || '', optionSize)
                                : null;
                              const sizeUnavailable = sizeStock !== null && sizeStock <= 0;

                              return (
                                <option key={optionSize} value={optionSize} disabled={sizeUnavailable}>
                                  {optionSize}{sizeUnavailable ? ' — Out of stock' : ''}
                                </option>
                              );
                            })}
                          </select>
                        ) : null}
                      </div>
                    );
                  })}
                </div>
              </section>

              <div className="grid gap-3 sm:grid-cols-2">
                <button
                  type="button"
                  onClick={() => handleAddToCart()}
                  disabled={isUnavailable || isAdding || !selectedOrderItems.length}
                  className={`inline-flex min-h-[54px] items-center justify-center gap-2 rounded-full px-5 text-base font-semibold transition ${
                    isUnavailable || !selectedOrderItems.length
                      ? 'cursor-not-allowed bg-gray-300 text-gray-500'
                      : 'bg-[#173822] text-white hover:bg-[#24552d]'
                  }`}
                >
                  <ShoppingCart size={18} />
                  {isAdding ? 'Adding...' : 'Add to Cart'}
                </button>

                <button
                  type="button"
                  onClick={() => handleAddToCart({ silent: true, redirectToCheckout: true })}
                  disabled={isUnavailable || isAdding || !selectedOrderItems.length}
                  className={`inline-flex min-h-[54px] items-center justify-center rounded-full border px-5 text-base font-semibold transition ${
                    isUnavailable || !selectedOrderItems.length
                      ? 'cursor-not-allowed border-gray-300 bg-gray-100 text-gray-400'
                      : 'border-[#173822] bg-white text-[#173822] hover:bg-[#f4fbf4]'
                  }`}
                >
                  Checkout
                </button>
              </div>

              <div className="grid gap-3 sm:grid-cols-3">
                <button
                  type="button"
                  onClick={() => {
                    const wasAdded = toggleWishlist(product);
                    toast.success(wasAdded ? 'Saved to wishlist!' : 'Removed from wishlist');
                  }}
                  className="inline-flex min-h-[52px] items-center justify-center gap-2 rounded-full border border-[#e6dcc8] bg-[#fffdfa] px-4 text-sm font-semibold text-[#182218] transition hover:border-[#ccb78c]"
                >
                  <Heart size={18} className={isFavorite ? 'fill-red-500 text-red-500' : 'text-[#536052]'} />
                  Wishlist
                </button>

                <a
                  href={`https://wa.me/254717002340?text=${encodeURIComponent(
                    buildOrderWhatsAppMessage({ product, selectedItems: selectedOrderItems })
                  )}`}
                  target="_blank"
                  rel="noreferrer"
                  className="inline-flex min-h-[52px] items-center justify-center gap-2 rounded-full border border-[#d9e8d6] bg-white px-4 text-sm font-semibold text-[#24552d] transition hover:border-[#24552d] hover:bg-[#f4fbf4]"
                >
                  <MessageCircle size={18} />
                  WhatsApp
                </a>

                <button
                  type="button"
                  onClick={async () => {
                    if (navigator.share) {
                      await navigator.share({
                        title: product.name,
                        text: `Check out ${product.name} - KES ${resolvedSalePrice}`,
                        url: typeof window !== 'undefined' ? window.location.href : buildProductPath(product),
                      });
                      return;
                    }

                    await navigator.clipboard?.writeText(
                      typeof window !== 'undefined' ? window.location.href : buildProductPath(product)
                    );
                    toast.success('Product link copied.');
                  }}
                  className="inline-flex min-h-[52px] items-center justify-center gap-2 rounded-full border border-[#e6dcc8] bg-[#fffdfa] px-4 text-sm font-semibold text-[#182218] transition hover:border-[#ccb78c]"
                >
                  <Share2 size={18} />
                  Share
                </button>
              </div>

              <section className="rounded-[1.5rem] border border-[#efe7d6] bg-[#fcfaf4] p-5">
                <div className="flex items-center justify-between gap-3">
                  <h2 className="text-lg font-semibold text-[#182218]">Quick facts</h2>
                  <span className="text-sm text-[#667063]">Quick facts</span>
                </div>
                <div className="mt-4 grid gap-3">
                  {detailRows.map((detail) => (
                    <div
                      key={detail.label}
                      className="flex items-start justify-between gap-4 border-b border-[#ede4d1] pb-3 last:border-b-0 last:pb-0"
                    >
                      <span className="text-sm text-[#667063]">{detail.label}</span>
                      <strong className="text-right text-sm font-semibold text-[#182218]">{detail.value}</strong>
                    </div>
                  ))}
                </div>
              </section>

              <section className="rounded-[1.5rem] border border-[#efe7d6] bg-[#fcfaf4] p-5">
                <div className="flex items-center justify-between gap-3">
                  <h2 className="text-lg font-semibold text-[#182218]">Customer reviews</h2>
                  <span className="text-sm text-[#667063]">{getReviewAggregate(reviews).averageRating || '0'} • {getReviewAggregate(reviews).reviewCount} reviews</span>
                </div>

                <div className="mt-4 space-y-4">
                  {reviews.length ? (
                    reviews.map((r) => (
                      <div key={r.id} className="rounded-xl border border-[#e6dcc8] bg-white p-3">
                        <div className="flex items-center justify-between">
                          <div>
                            <p className="font-semibold text-[#182218]">{r.name}</p>
                            <p className="text-sm text-[#667063]">{r.location} • {r.date || ''}</p>
                          </div>
                          <div className="text-sm font-semibold text-[#24552d]">{r.rating} ⭐</div>
                        </div>
                        <p className="mt-2 text-sm text-[#62675f]">{r.text}</p>
                      </div>
                    ))
                  ) : (
                    <p className="text-sm text-[#5f5b55]">No reviews yet. Be the first to review this product.</p>
                  )}
                </div>

                <div className="mt-4">
                  <h3 className="text-sm font-semibold text-[#182218]">Leave a review</h3>
                  <textarea
                    value={reviewText}
                    onChange={(e) => setReviewText(e.target.value)}
                    className="mt-2 w-full rounded-xl border border-[#e6dcc8] bg-white p-3 text-sm text-[#182218]"
                    rows={4}
                    placeholder="Share your experience with this product"
                  />

                  <div className="mt-2 flex items-center gap-2">
                    <label className="text-sm text-[#667063]">Rating</label>
                    <select value={reviewRating} onChange={(e) => setReviewRating(Number(e.target.value))} className="rounded-lg border border-[#e6dcc8] bg-white px-3 py-1 text-sm">
                      {[5,4,3,2,1].map((n) => (
                        <option key={n} value={n}>{n} star{n>1?'s':''}</option>
                      ))}
                    </select>
                    <button
                      type="button"
                      disabled={reviewSubmitting || !reviewText.trim()}
                      onClick={async () => {
                        if (!product?.id) return;
                        setReviewSubmitting(true);
                        try {
                          const payload = {
                            productId: product.id,
                            name: (user?.email || 'Verified customer'),
                            location: '',
                            rating: reviewRating,
                            text: reviewText.trim(),
                            uid: user?.uid || null,
                          };

                          const resp = await fetch('/api/product-reviews', {
                            method: 'POST',
                            headers: { 'Content-Type': 'application/json' },
                            body: JSON.stringify(payload),
                          });

                          if (!resp.ok) throw new Error('Could not submit review');
                          const created = await resp.json();
                          setReviews((prev) => [created, ...prev]);
                          setReviewText('');
                          setReviewRating(5);
                          toast.success('Thanks — your review was submitted.');
                        } catch (err) {
                          toast.error(err?.message || 'Failed to submit review.');
                        } finally {
                          setReviewSubmitting(false);
                        }
                      }}
                      className="ml-auto rounded-full bg-[#173822] px-4 py-2 text-sm font-semibold text-white"
                    >
                      {reviewSubmitting ? 'Submitting...' : 'Submit review'}
                    </button>
                  </div>
                </div>
              </section>

              {productDetails.length ? (
                <section className="rounded-[1.5rem] border border-[#efe7d6] bg-[#fcfaf4] p-5">
                  <div className="flex items-center justify-between gap-3">
                    <h2 className="text-lg font-semibold text-[#182218]">Product details</h2>
                    <span className="text-sm text-[#667063]">Extra info</span>
                  </div>
                  <div className="mt-4 grid gap-3">
                    {productDetails.map((detail) => (
                      <div
                        key={detail.key}
                        className="flex items-start justify-between gap-4 border-b border-[#ede4d1] pb-3 last:border-b-0 last:pb-0"
                      >
                        <span className="text-sm text-[#667063]">{detail.label || 'Detail'}</span>
                        <strong className="text-right text-sm font-semibold text-[#182218]">{detail.value}</strong>
                      </div>
                    ))}
                  </div>
                </section>
              ) : null}

              {descriptionContent.sections.map((section) => (
                <section
                  key={section.key}
                  className="rounded-[1.5rem] border border-[#efe7d6] bg-[#fcfaf4] p-5"
                >
                  <div className="flex items-center justify-between gap-3">
                    <h2 className="text-lg font-semibold text-[#182218]">{section.title}</h2>
                    <span className="text-sm text-[#667063]">More info</span>
                  </div>

                  {section.items?.length ? (
                    <div className="mt-4 grid gap-3">
                      {section.items.map((item, index) => (
                        <div
                          key={`${section.key}-${item.label || 'item'}-${index}`}
                          className="flex items-start justify-between gap-4 border-b border-[#ede4d1] pb-3 last:border-b-0 last:pb-0"
                        >
                          <span className="text-sm text-[#667063]">
                            {item.label || 'Detail'}
                          </span>
                          <strong className="text-right text-sm font-semibold text-[#182218]">
                            {item.value}
                          </strong>
                        </div>
                      ))}
                    </div>
                  ) : section.text ? (
                    <p className="mt-4 text-sm leading-7 text-[#62675f] sm:text-base">
                      {section.text}
                    </p>
                  ) : null}
                </section>
              ))}
            </div>
          </div>

          <section className="space-y-6 border-t border-[#e5decc] pt-10">
            <div className="flex flex-col gap-2 sm:flex-row sm:items-end sm:justify-between">
              <div>
                <p className="text-sm font-semibold uppercase tracking-[0.24em] text-[#2b5f34]">Keep Browsing</p>
                <h2 className="text-3xl font-playfair font-bold text-[#162218]">You may also like</h2>
              </div>
              <Link
                href={buildCatalogPath({ department, subcategory: hasSubdivision ? subdivision : '' })}
                className="text-sm font-semibold text-[#24552d] transition hover:text-[#173822]"
              >
                Back to {hasSubdivision ? subdivision : department}
              </Link>
            </div>

            <div className="grid grid-cols-2 gap-3 sm:gap-4 lg:grid-cols-4">
              {recommendedProducts.map((item) => {
                const {
                  salePrice: itemSalePrice,
                  regularPrice: itemOriginalPrice,
                  hasDiscount: itemHasDiscount,
                  discount: itemDiscount,
                } = getProductPricing(item);

                return (
                  <Link
                    key={item.id}
                    href={buildProductPath(item)}
                    className="group overflow-hidden rounded-3xl border border-[#e7e1d3] bg-white shadow-[0_16px_45px_rgba(17,17,17,0.06)] transition hover:-translate-y-1 hover:shadow-[0_24px_70px_rgba(17,17,17,0.12)] max-[360px]:rounded-[1.35rem]"
                  >
                    <div className="relative h-64 overflow-hidden bg-gray-100 max-[360px]:h-40">
                      <Image
                        src={item.image || '/homepage banner.jpg'}
                        alt={item.name}
                        fill
                        className="object-cover transition duration-500 group-hover:scale-105"
                      />
                    </div>
                    <div className="space-y-2 p-4 text-left max-[360px]:space-y-1.5 max-[360px]:p-3">
                      <p className="text-xs font-semibold uppercase tracking-[0.3em] text-teal-700 max-[360px]:text-[10px] max-[360px]:tracking-[0.14em]">
                        {formatProductCategory(item) || item.department || item.category || 'Footwear'}
                      </p>
                      <h3 className="text-lg font-semibold text-gray-900 max-[360px]:text-sm">{item.name}</h3>
                      <div className="flex flex-wrap items-center gap-2 text-sm text-gray-600 max-[360px]:text-xs">
                        <span className="font-semibold text-[#1c6a43]">KES {itemSalePrice}</span>
                        {itemHasDiscount ? (
                          <>
                            <span className="line-through text-gray-400">KES {itemOriginalPrice}</span>
                            <span className="rounded-full bg-[#eef7ef] px-2 py-1 text-[10px] font-semibold uppercase tracking-[0.12em] text-[#24552d]">
                              -{itemDiscount}%
                            </span>
                          </>
                        ) : null}
                      </div>
                    </div>
                  </Link>
                );
              })}
            </div>
          </section>
        </div>
      </div>
    </motion.div>
  );
}

ProductDetailPageView.propTypes = {
  product: PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
    name: PropTypes.string.isRequired,
    description: PropTypes.string,
    details: PropTypes.string,
    category: PropTypes.string,
    subcategory: PropTypes.string,
    sizeType: PropTypes.string,
    image: PropTypes.string,
    isOutOfStock: PropTypes.bool,
    stock: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    sku: PropTypes.string,
    price: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
    basePrice: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    salePrice: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    originalPrice: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    discount: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    sizes: PropTypes.arrayOf(PropTypes.string),
    variants: PropTypes.arrayOf(
      PropTypes.shape({
        name: PropTypes.string,
        image: PropTypes.string,
      })
    ),
  }),
  recommendedProducts: PropTypes.arrayOf(PropTypes.object),
  selectedColorSlug: PropTypes.string,
};
